import { c as _c } from "react/compiler-runtime";
import { IcoChevronDown16 } from '@onefootprint/icons';
import * as RadixPopover from '@radix-ui/react-popover';
import { cx } from 'class-variance-authority';
import { AnimatePresence, easeOut, motion } from 'framer-motion';
import * as React from 'react';
import Chip from '../chip';
export type InputProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  selectedValues: string[];
  options: Array<{
    label: string;
    value: string;
  }>;
  placeholder: string;
  hasError: boolean;
  disabled: boolean;
  isPopoverOpen: boolean;
  onKeyDown: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
  toggleOption: (value: string) => void;
};
export const Input = React.forwardRef<HTMLButtonElement, InputProps>((t0, ref) => {
  const $ = _c(41);
  let isPopoverOpen;
  let onKeyDown;
  let options;
  let props;
  let selectedValues;
  let t1;
  let t2;
  let t3;
  let toggleOption;
  if ($[0] !== t0) {
    ({
      selectedValues,
      options,
      placeholder: t1,
      hasError: t2,
      disabled: t3,
      isPopoverOpen,
      onKeyDown,
      toggleOption,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = isPopoverOpen;
    $[2] = onKeyDown;
    $[3] = options;
    $[4] = props;
    $[5] = selectedValues;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = toggleOption;
  } else {
    isPopoverOpen = $[1];
    onKeyDown = $[2];
    options = $[3];
    props = $[4];
    selectedValues = $[5];
    t1 = $[6];
    t2 = $[7];
    t3 = $[8];
    toggleOption = $[9];
  }
  const placeholder = t1 === undefined ? "Select options" : t1;
  const hasError = t2 === undefined ? false : t2;
  const disabled = t3 === undefined ? false : t3;
  const t4 = !hasError && !disabled;
  const t5 = !disabled;
  let t6;
  if ($[10] !== disabled || $[11] !== hasError || $[12] !== t4 || $[13] !== t5) {
    t6 = cx("w-full min-w-[240px]", "flex items-center justify-between w-full h-full p-1 border rounded-default group min-h-9 gap-x-2 focus-visible:outline-[var(--text-color-accent)] text-body-2", {
      "border-primary hover:border-primary": t4,
      "border-tertiary bg-secondary cursor-default": disabled,
      "cursor-pointer": t5,
      "border-error": hasError
    });
    $[10] = disabled;
    $[11] = hasError;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== options || $[16] !== placeholder || $[17] !== selectedValues || $[18] !== toggleOption) {
    t7 = selectedValues.length > 0 ? selectedValues.map(value => {
      const option = options.find(o => o.value === value);
      return <motion.div key={value} layout={true} initial={{
        opacity: 0,
        scale: 0.8
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.8
      }} transition={{
        duration: 0.2,
        ease: easeOut
      }}><Chip label={option?.label} value={value} toggleOption={toggleOption} /></motion.div>;
    }) : <span className="flex items-center mx-3 text-body-2 text-tertiary">{placeholder}</span>;
    $[15] = options;
    $[16] = placeholder;
    $[17] = selectedValues;
    $[18] = toggleOption;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t7) {
    t8 = <motion.div className="flex flex-wrap gap-1"><AnimatePresence mode="popLayout">{t7}</AnimatePresence></motion.div>;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const t9 = isPopoverOpen ? 180 : 0;
  let t10;
  if ($[22] !== t9) {
    t10 = {
      rotate: t9
    };
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      duration: 0.2,
      ease: easeOut
    };
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  const t12 = disabled ? "tertiary" : "primary";
  let t13;
  if ($[25] !== t12) {
    t13 = <IcoChevronDown16 color={t12} />;
    $[25] = t12;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t10 || $[28] !== t13) {
    t14 = <motion.div className="flex items-center justify-between mr-2" animate={t10} transition={t11}>{t13}</motion.div>;
    $[27] = t10;
    $[28] = t13;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== disabled || $[31] !== isPopoverOpen || $[32] !== onKeyDown || $[33] !== t14 || $[34] !== t6 || $[35] !== t8) {
    t15 = <button className={t6} tabIndex={0} onKeyDown={onKeyDown} aria-pressed={isPopoverOpen} type="button" aria-haspopup="listbox" aria-controls="multi-select-content" aria-expanded={isPopoverOpen} disabled={disabled} data-disabled={disabled}>{t8}{t14}</button>;
    $[30] = disabled;
    $[31] = isPopoverOpen;
    $[32] = onKeyDown;
    $[33] = t14;
    $[34] = t6;
    $[35] = t8;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== props || $[38] !== ref || $[39] !== t15) {
    t16 = <RadixPopover.Trigger {...props} ref={ref} tabIndex={0} asChild={true}>{t15}</RadixPopover.Trigger>;
    $[37] = props;
    $[38] = ref;
    $[39] = t15;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  return t16;
});
export default Input;